// Shoe sizes are shown with fractions, never raw decimals (42½, 45⅓, 9½).
// The backend sends display strings (`size_display`, `size_uk_display`,
// `size_us_display`); `formatSize` is only a fallback for values we hold
// locally (e.g. the size stepper in the check-in).

const GLYPHS = { "½": 1 / 2, "¼": 1 / 4, "¾": 3 / 4, "⅓": 1 / 3, "⅔": 2 / 3 };
const FRACTION_TEXT = [
  [0, ""],
  [1 / 4, "¼"],
  [1 / 3, "⅓"],
  [1 / 2, "½"],
  [2 / 3, "⅔"],
  [3 / 4, "¾"],
];

// 42.5 -> "42½", 45.333 -> "45⅓", 42 -> "42". Unknown fractions fall back to
// the nearest supported glyph (sizes never come in tenths).
export function formatSize(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = typeof value === "number" ? value : parseSizeInput(String(value));
  if (n === null || !Number.isFinite(n)) return String(value);
  let whole = Math.floor(n + 1e-6);
  const frac = n - whole;
  let best = FRACTION_TEXT[0];
  let bestDiff = Infinity;
  FRACTION_TEXT.forEach((f) => {
    const d = Math.abs(frac - f[0]);
    if (d < bestDiff) {
      bestDiff = d;
      best = f;
    }
  });
  if (Math.abs(frac - 1) < bestDiff) {
    whole += 1;
    best = FRACTION_TEXT[0];
  }
  return `${whole}${best[1]}`;
}

// Prefer the backend's display string; fall back to formatting the number.
export function sizeText(display, raw) {
  if (display) return display;
  if (raw === null || raw === undefined || raw === "") return "";
  return formatSize(raw);
}

// "42", "42,5", "42.5", "42 2/3", "42⅔" -> number, else null.
export function parseSizeInput(text) {
  let t = String(text ?? "").trim().replace(",", ".");
  if (!t) return null;
  let add = 0;
  Object.entries(GLYPHS).forEach(([g, v]) => {
    if (t.includes(g)) {
      add += v;
      t = t.split(g).join("");
    }
  });
  t = t.trim();
  const m = t.match(/^(\d{1,2}(?:\.\d{1,3})?)(?:\s+(\d)\s*\/\s*(\d))?$/);
  if (!m) {
    if (!t && add) return add;
    return null;
  }
  let v = parseFloat(m[1]);
  if (m[2]) {
    const den = parseInt(m[3], 10);
    if (!den) return null;
    v += parseInt(m[2], 10) / den;
  }
  return v + add;
}

// Steps per size system: EU has thirds and halves, UK/US halves only.
const STEP_FRACTIONS = { EU: [0, 1 / 3, 1 / 2, 2 / 3], UK: [0, 1 / 2], US: [0, 1 / 2] };
export const SIZE_RANGE = { EU: [16, 52], UK: [1, 15], US: [1, 16] };
export const SIZE_DEFAULT = { EU: 42, UK: 8, US: 9 };

function stepsFor(unit) {
  const fr = STEP_FRACTIONS[unit] || STEP_FRACTIONS.EU;
  const [lo, hi] = SIZE_RANGE[unit] || SIZE_RANGE.EU;
  const out = [];
  for (let w = lo; w <= hi; w++) {
    fr.forEach((f) => {
      if (w + f <= hi + 1e-6) out.push(w + f);
    });
  }
  return out;
}

// Snap any typed value to the nearest real size step for that system.
export function snapSize(value, unit) {
  if (value === null || !Number.isFinite(value)) return null;
  const steps = stepsFor(unit);
  const [lo, hi] = SIZE_RANGE[unit] || SIZE_RANGE.EU;
  if (value < lo - 0.5 || value > hi + 0.5) return null;
  let best = steps[0];
  steps.forEach((s) => {
    if (Math.abs(s - value) < Math.abs(best - value)) best = s;
  });
  return best;
}

// Next / previous step. From "no value" the first press lands on the default.
export function stepSize(value, unit, dir) {
  const steps = stepsFor(unit);
  if (value === null || value === undefined) return SIZE_DEFAULT[unit] ?? steps[0];
  const idx = steps.findIndex((s) => Math.abs(s - value) < 0.01);
  if (idx === -1) return snapSize(value, unit);
  const next = Math.min(steps.length - 1, Math.max(0, idx + dir));
  return steps[next];
}
